import { m as motion, useReducedMotion } from "framer-motion";
import { ReactNode } from "react";

/**
 * Canonical hero reveal cascade — applied to every hero on the site.
 * Eyebrow first, headline 50ms later, body 150ms, CTA row 250ms.
 * Total settle ~650ms. Do NOT redefine this per-page; import and use.
 */
export const HERO_CASCADE = {
  eyebrow: 0,
  headline: 0.05,
  body: 0.15,
  cta: 0.25,
} as const;

interface RevealProps {
  children: ReactNode;
  /** Stagger delay in seconds before this element animates. */
  delay?: number;
  /** Distance to translate up from, in pixels. Default 20. */
  distance?: number;
  /** Animation duration in seconds. Default 0.4. */
  duration?: number;
  className?: string;
  as?: "div" | "section" | "article" | "li" | "span";
}

/**
 * Editorial reveal-on-scroll wrapper.
 * Fades in + translates up 20px once when in view. Respects prefers-reduced-motion.
 * Use anywhere you'd otherwise reach for an inline framer-motion block.
 */
export const Reveal = ({
  children,
  delay = 0,
  distance = 20,
  duration = 0.4,
  className,
  as = "div",
}: RevealProps) => {
  const prefersReducedMotion = useReducedMotion();
  const MotionTag = motion[as];

  if (prefersReducedMotion) {
    return <MotionTag className={className}>{children}</MotionTag>;
  }

  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y: distance }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{
        duration,
        delay,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      {children}
    </MotionTag>
  );
};

